import * as React from 'react';
import AreaEntity from '../domain/AreaEntity';
import { useTranslation, Trans } from 'react-i18next';
import { ConfigProvider, Form, message, Typography } from 'antd';
import { observer } from 'mobx-react-lite';
import CardContainer from '../../../components/CardContainer';
import StepperFooter from '../../../components/StepperFooter';
import CategoryKeywordModel from '../models/CategoryKeywordModel';
import BusinessModel from '../../FindYourBusiness/models/BusinessModel';
import AntdCustomCheckbox from '../../../components/buttons/AntdCustomCheckbox';
import CustomTagsField from '../../../components/CustomTagsField';
import { BodyText } from '../../../components/ui/Typography';
import HelpBoxCompanionModel from '../../../models/HelpBoxCompanionModel';
import { AnimatePresence, motion } from 'framer-motion';
import {
  fadeInUpVariants,
  staggerContainerVariants
} from '../../../components/animations/StepAnimations';

const { Text } = Typography;

interface FCProps {
  stepData: {};
}

const AreasStep: React.FC<FCProps> = observer(() => {
  const { t, i18n } = useTranslation();
  const [isStepLoading, setIsStepLoading] = React.useState(false);
  const [isVisible, setIsVisible] = React.useState(true);
  const [hasCustomTagErrors, setHasCustomTagErrors] = React.useState(false);

  const areas = CategoryKeywordModel.areas;
  const [form] = Form.useForm();

  const allCustomAreas = areas.filter(
    (area) => area.isChecked && area.isCustomArea
  );
  const allNonCustomAreas = areas.filter(
    (area) => area.isChecked && !area.isCustomArea
  );

  const disabledUncheckedAreas =
    allCustomAreas.length + allNonCustomAreas.length === 3;

  const helpBoxTitleMessage = t('Why is that important?'); 
  const helpBoxParagraphMessage = t('Your corporate category describes your entrepreneurial activities not very precisely. In order to create the one optimal website you should describe your activities a little more precisely. You can use our suggestions or own activities.');

  React.useEffect(() => {
    HelpBoxCompanionModel.setTitleMessage(helpBoxTitleMessage);
    HelpBoxCompanionModel.setParagraphMessage(helpBoxParagraphMessage);
  }, [i18n.language]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial="hidden"
          animate="visible"
          exit="hidden"
          variants={staggerContainerVariants}>
          <CardContainer title={t('Focus')}>
            <motion.div variants={fadeInUpVariants}>
              <BodyText style={{ textAlign: 'center' }}>
                <Trans
                  i18nKey="areaStep.description1"
                  defaults="Your business category <b>{{category}}</b> doesn't describe your business activities very precisely."
                  components={{ b: <b /> }}
                  values={{ category: BusinessModel.getWizardData().category }}
                />
                <br />
                <Trans
                  i18nKey="areaStep.description2"
                  defaults="Please also select <b>1-3 relevant areas</b> of expertise."
                  components={{ b: <b /> }}
                />
              </BodyText>
            </motion.div>
            <div className="sm:text-center">
              <div className="flex flex-col">
                <div>
                  <div className="mb-5">
                    <ConfigProvider
                      theme={{
                        token: {
                          fontSize: 12
                        }
                      }}></ConfigProvider>
                  </div>
                  <div className="flex flex-col gap-5">
                    <motion.div
                      variants={fadeInUpVariants}
                      className="flex flex-wrap gap-4 justify-start sm:justify-center">
                      {areas
                        .filter((area) => !area.isCustomArea)
                        .map((area, index) => (
                          <AntdCustomCheckbox
                            key={index}
                            appearance="basic"
                            checked={area.isChecked}
                            disabled={disabledUncheckedAreas && !area.isChecked}
                            onChange={(e) => {
                              area.setChecked(e.target.checked);
                              if (e.target.checked) {
                                CategoryKeywordModel.removeCustomAreaByName(
                                  area.displayName.toLowerCase()
                                );
                              }
                            }}>
                            <Text>{area.displayName}</Text>
                          </AntdCustomCheckbox>
                        ))}
                    </motion.div>
                    <motion.div
                      variants={fadeInUpVariants}
                      className="flex sm:justify-center">
                      <CustomTagsField
                        form={form}
                        formItemName="customAreas"
                        initialValues={areas
                          .filter((a) => a.isCustomArea)
                          .map((a) => a.displayName)}
                        addButtonLabel={t('Add your own focus')}
                        addFurtherButtonLabel={t('Add further focus')}
                        tagsTitle={t('Define your own focus :')}
                        maxCount={3 - allNonCustomAreas.length}
                        disabled={disabledUncheckedAreas}
                        placeholder={t('Press [Enter] to add your own focus.')}
                        duplicateMessage={t('This focus already exists')}
                        existingValues={areas
                          .filter((a) => a.isChecked && !a.isCustomArea)
                          .map((a) => a.displayName.toLowerCase())}
                        onChange={(value) => {
                          CategoryKeywordModel.removeAllCustomAreas();
                          value.forEach((area) => {
                            CategoryKeywordModel.addNewArea(
                              new AreaEntity({
                                checked: true,
                                displayName: area,
                                name: area,
                                keywords: [],
                                isCustom: true
                              })
                            );
                          });
                        }}
                        onValidationStateChange={(hasErrors) =>
                          setHasCustomTagErrors(hasErrors)
                        }
                      />
                    </motion.div>
                  </div>
                </div>
              </div>
            </div>
            <motion.div variants={fadeInUpVariants}>
              <StepperFooter
                buttonId="category-save-next"
                buttonGaEvent="category-save-next"
                loading={isStepLoading}
                disabled={
                  areas.filter((area) => area.isChecked).length === 0 ||
                  hasCustomTagErrors
                }
                onFormsValidationSuccess={async (
                  formData,
                  nextStep,
                  toStep
                ) => {
                  setIsStepLoading(true);

                  const allCheckedAreas = areas.filter(
                    (area) => area.isChecked
                  );

                  if (allCheckedAreas.length === 0) {
                    message.error(
                      t('Please select at least one focus or add a custom one.')
                    );
                    setIsStepLoading(false);
                    return;
                  }
                  try {
                    await form.validateFields();
                  } catch (error) {
                    console.error('Form validation failed:', error);
                    message.error(
                      t('Please select at least one focus or add a custom one.')
                    );
                    setIsStepLoading(false);
                    return;
                  }

                  try {
                    await CategoryKeywordModel.fetchKeywordsForCustomAreas();
                    setIsVisible(false); 
                    setTimeout(async () => {
                      await CategoryKeywordModel.moveToNextStep();
                    }, 200); 
                  } catch (error) {
                    message.error(
                      t(
                        'An error occurred while fetching keywords for custom areas. Please try again.'
                      )
                    );
                  } finally {
                    setIsStepLoading(false);
                  }
                }}
                count={areas.filter((area) => area.isChecked).length}
                maxCount={3}
                label={t('focus')}
                counterText={`${
                  areas.filter((area) => area.isChecked).length
                }/3 ${t('focus')} ${t('defined')}`}
              />
            </motion.div>
          </CardContainer>
        </motion.div>
      )}
    </AnimatePresence>
  );
});

export default AreasStep;
